import { useEffect } from "react";
import axios from "axios";
import moment from "moment-timezone";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import MaximizeIcon from "../../../components/icons/MaximizeIcon";
import {
  OnestBoldBig,
  OnestNormalDefault,
  OnestSemiBoldDefault,
} from "../../../components/styled/TextComponents";
import Config from "../../../Config";
import {
  color_black,
  color_grey_dark,
  color_red_default,
  color_transparent,
  color_white,
} from "../../../constants/colors";
import { updateDayLessonData } from "../../../store/ScheduleDayExtendedSlice";
import getRussianNumberEnding from "../../../utilities/rusNumbers";
import { useWindowDimensions } from "../../../utils";
import LessonPreview from "./LessonPreview";
import PersonalEventPreview from "./PersonalEventPreview";
import {
  getNormalizedWeekDayMonFirst,
  MOBILE_CALENDAR_WIDTH,
} from "./scheduleConstants";
import { useI18n } from "../../../i18n/I18nProvider";

const TIME_RANGES = [
  { start: "00:00", end: "10:00" },
  { start: "10:00", end: "14:00" },
  { start: "14:00", end: "18:00" },
  { start: "18:00", end: "21:00" },
  { start: "21:00", end: "24:00" },
];

const ScheduleDayHeader = ({ weekday, lessonsCount }) => {
  const { t } = useI18n();
  const weekdays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
  const weekDayString = weekdays[getNormalizedWeekDayMonFirst(weekday.day())];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        marginBottom: 10,
      }}
    >
      <OnestSemiBoldDefault>{t(weekDayString)}</OnestSemiBoldDefault>
      <div
        style={{
          width: 6,
          height: 6,
          backgroundColor: color_grey_dark,
          borderRadius: "50%",
          margin: "0 8px",
        }}
      />
      <OnestNormalDefault style={{ color: color_grey_dark }}>
        {" "}
        {t("{{count}} урок{{ending}}", {
          count: lessonsCount,
          ending: getRussianNumberEnding(lessonsCount),
        })}
      </OnestNormalDefault>
    </div>
  );
};

const ScheduleDayBodyHeader = ({ day, weekday, lessons, isToday }) => {
  const { width: screenWidth } = useWindowDimensions();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isWeekend = [6, 0].includes(weekday.day());

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          width: 60,
          height: 60,
          backgroundColor: isWeekend ? color_red_default : color_transparent,
          color: isWeekend ? color_white : color_black,
          borderRadius: 10,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          boxSizing: "border-box",
          ...(screenWidth >= MOBILE_CALENDAR_WIDTH && isToday
            ? isWeekend
              ? { boxShadow: `inset 0 0 0 2px ${color_black}` }
              : { boxShadow: `inset 0 0 0 2px ${color_red_default}` }
            : {}),
        }}
      >
        <OnestBoldBig style={{ fontSize: 40 }}>
          {day.date() < 10 ? `0${day.date()}` : `${day.date()}`}
        </OnestBoldBig>
      </div>
      {screenWidth >= MOBILE_CALENDAR_WIDTH && (
        <div
          style={{
            width: 25,
            height: 25,
            backgroundColor: color_red_default,
            color: color_white,
            borderRadius: "50%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            alignContent: "center",
            fontSize: 10,
            marginTop: 5,
            marginRight: 5,
            cursor: "pointer",
          }}
          onClick={() => {
            dispatch(
              updateDayLessonData({
                date: moment(day).toISOString(),
                lessons,
              }),
            );
            navigate("/service/schedule/day");
          }}
        >
          <MaximizeIcon width={18} height={18} />
        </div>
      )}
    </div>
  );
};

const ScheduleDayBody = ({
  day,
  weekday,
  lessons,
  isToday,
  onOpenModalNote,
  onOpenModalReschedule,
  onOpenPersonalEventEdit,
  onDeletePersonalEvent,
  openCardId,
  onToggleCard,
}) => {
  const navigate = useNavigate();
  const newLessons = [...lessons].sort(
    (a, b) => moment(a.dateDate).valueOf() - moment(b.dateDate).valueOf(),
  );
  const lessonsCount = newLessons.length;

  const renderLessonsForTimeRange = (start, end) => {
    const filteredLessons = newLessons.filter((lesson) => {
      const [lessonStart] = lesson.timeSlot.split(/\s*-\s*/);
      return lessonStart >= start && lessonStart < end;
    });

    if (filteredLessons.length > 0) {
      return filteredLessons.map((el) =>
        el.eventType === "personal" ? (
          <PersonalEventPreview
            key={el.id}
            eventData={el}
            onOpenPersonalEventEdit={onOpenPersonalEventEdit}
            onDeletePersonalEvent={onDeletePersonalEvent}
            isMenuOpen={openCardId === el.id}
            onToggleMenu={() => onToggleCard(el.id)}
          />
        ) : (
          <LessonPreview
            key={el.id}
            lessonData={el}
            onOpenModalNote={onOpenModalNote}
            onOpenModalReschedule={onOpenModalReschedule}
            lessonsCount={lessonsCount}
            isMenuOpen={openCardId === el.id}
            onToggleMenu={() => onToggleCard(el.id)}
          />
        ),
      );
    }
    return <div key={start} style={{ display: "flex", flex: 1 }} />;
  };

  useEffect(() => {
    axios
      .get(Config.BACKEND_ADDR + "/payments/getSubscriptionPeriod")
      .then((response) => {
        if (response.status === 200 && response.data.status === "not_paid") {
          navigate("/billing/");
        }
      })
      .catch((err) => {
        console.warn("Ошибка проверки подписки в БД! ", err);
      });
  }, [navigate]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        flex: 1,
        gap: 5,
        padding: 5,
        boxSizing: "border-box",
      }}
    >
      <ScheduleDayBodyHeader
        day={day}
        weekday={weekday}
        lessons={lessons}
        isToday={isToday}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          flex: 1,
          minHeight: 0,
          gap: 8,
        }}
      >
        {TIME_RANGES.map(({ start, end }) =>
          renderLessonsForTimeRange(start, end),
        )}
      </div>
    </div>
  );
};

const ScheduleDay = ({
  weekday,
  day,
  lessons,
  isToday,
  onOpenModalNote,
  onOpenModalReschedule,
  onOpenPersonalEventEdit,
  onDeletePersonalEvent,
  openCardId,
  onToggleCard,
}) => {
  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        minWidth: 150,
        minHeight: 0,
      }}
    >
      <ScheduleDayHeader weekday={weekday} lessonsCount={lessons.length} />
      <div
        style={{
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          backgroundColor: color_white,
          borderRadius: 10,
        }}
      >
        <ScheduleDayBody
          day={day}
          weekday={weekday}
          isToday={isToday}
          onOpenModalNote={onOpenModalNote}
          onOpenModalReschedule={onOpenModalReschedule}
          onOpenPersonalEventEdit={onOpenPersonalEventEdit}
          onDeletePersonalEvent={onDeletePersonalEvent}
          lessons={lessons}
          openCardId={openCardId}
          onToggleCard={onToggleCard}
        />
      </div>
    </div>
  );
};

export default ScheduleDay;
